import React, {ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import SidebarLeftMode from "../settings/SidebarLeftMode";
import {Button} from "primereact/button";
import Resizeable, {ResizeableDirection} from "../../../shared/components/Resizeable";
import DocumentSettings from "../../document/DocumentSettings";
import {PortalGlobals} from "../../Portal";
import PageList from "../../document/components/pdf/PageList";
import AttachmentList from "../../document/components/pdf/AttachmentList";
import OutlineList from "../../document/components/pdf/OutlineList";
import SignatureList from "../../document/components/pdf/SignatureList";
import TextSearch, {TextSearchGlobals} from "../../document/components/pdf/TextSearch";
import LengthUnit from "../../../shared/units/LengthUnit";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import Observer from "../../../shared/class/Observer";
import DestinationEvent from "../DestinationEvent";
import LocationEvent from "../LocationEvent";
import Immutable from "../../../shared/immutable/Immutable";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {SidebarFunctionGlobals} from "../../document/components/pdf/SidebarFunctions";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";
import ViewerType from "../../document/DataType";

export interface LeftSidebarProperties extends PortalGlobals, I18nGlobals, TextSearchGlobals, SidebarFunctionGlobals,
	DebugGlobals {
	portalDocument?: Immutable<PortalDocumentInterface>;
	viewerScreen: RefObject<HTMLDivElement>;
	destinationObserver?: Observer<DestinationEvent>;
	locationObserver?: Observer<LocationEvent>;
}

export interface LeftSidebarState {
	sidebarLeftWidth: string;
}

class LeftSidebarDefinition extends PortalPureComponent<LeftSidebarProperties, LeftSidebarState> {
	/**
	 * Left sidebar component constructor
	 */
	constructor(props: Readonly<LeftSidebarProperties>) {
		super(props);

		this.state = {
			sidebarLeftWidth: 350 + LengthUnit.PIXEL
		};

		this.changeLeftSidebarMode = this.changeLeftSidebarMode.bind(this);
	}

	/**
	 * Change the sidebar left mode to global settings
	 */
	private changeLeftSidebarMode(mode: SidebarLeftMode): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		viewerSettings.sidebarLeftMode = viewerSettings.sidebarLeftMode === mode ? SidebarLeftMode.NONE : mode;
		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private isPdf(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let isPdf: boolean = false;
		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
			);

			if (FunctionHelper.exists(attachment)) {
				isPdf = portalDocument.getViewerType(attachment!) === ViewerType.PDF;
			}
		} else {
			isPdf = portalDocument.getViewerType() === ViewerType.PDF;
		}

		return isPdf;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (!portalDocument.isViewable() || !this.isPdf()) {
			return null;
		}

		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		return (
			<div className={"sidebar left" + (viewerSettings.sidebarLeftMode !== SidebarLeftMode.NONE ? " in" : "")}
				 style={{width: this.state.sidebarLeftWidth}}>
				<Button className="toggle-btn" icon="icon icon-angle-left"
						onClick={() => this.changeLeftSidebarMode(SidebarLeftMode.NONE)}/>
				<Resizeable direction={ResizeableDirection.HORIZONTAL}
							resizeCallback={
								(options: { width: string }) => {
									this.setState({
										sidebarLeftWidth: options.width
									});
								}
							}
							container={this.props.viewerScreen} invert={false}/>
				<PageList {...this.props} visible={viewerSettings.sidebarLeftMode === SidebarLeftMode.PAGES}/>
				<AttachmentList {...this.props}
								visible={viewerSettings.sidebarLeftMode === SidebarLeftMode.ATTACHMENTS}/>
				<OutlineList {...this.props}
							 visible={viewerSettings.sidebarLeftMode === SidebarLeftMode.OUTLINES}/>
				<SignatureList {...this.props}
							   visible={viewerSettings.sidebarLeftMode === SidebarLeftMode.SIGNATURES}/>
				<TextSearch {...this.props} visible={viewerSettings.sidebarLeftMode === SidebarLeftMode.SEARCH}/>
			</div>
		);
	}
}

const LeftSidebar = React.memo(LeftSidebarDefinition,
	(prevProps: Readonly<LeftSidebarProperties>, nextProps: Readonly<LeftSidebarProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSearchResults) &&
			!nextProps.textSearchResults!.equals(prevProps.textSearchResults)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.dragEvent) && !nextProps.dragEvent!.equals(prevProps.dragEvent)) {
			return false;
		}

		if (nextProps.textSearchSelection !== prevProps.textSearchSelection) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		return true;
	}
);

export default LeftSidebar;